import { useTranslation } from 'react-i18next';

import { useFilterPreferencesConfig } from '@/shared/features/filter-preferences/context/filter-preferences-config-context';
import {
  TPreferencesLayer,
  TPreferencesSnapshot,
} from '@/shared/features/filter-preferences/context/filter-preferences-config-context.types';
import {
  TPatchOptions,
  TUiPreferencesWriter,
} from '@/shared/features/filter-preferences/hooks/use-ui-preferences-writer/use-ui-preferences-writer.types';
import {
  commitPreferences,
  removeFilterDefaultFromDefaults,
  setFilterDefaultInDefaults,
} from '@/shared/features/filter-preferences/hooks/use-ui-preferences-writer/use-ui-preferences-writer.utils';
import { useSaveToast } from '@/shared/hooks/use-save-toast/use-save-toast';
import { selectUser } from '@/shared/redux/api/auth/auth-api';
import {
  useReadUserUiPreferencesQuery,
  useUpdateUserUiPreferencesMutation,
} from '@/shared/redux/api/ident/ident-api';
import { useAppSelector } from '@/shared/redux/hooks/hooks';
import { ReadUserUiPreferencesQuery } from '@/shared/types/types.generated';
import {
  TFilterDefaults,
  TTenantUiPreferences,
  TUserUiPreferences,
} from '@/shared/types/ui-preferences.types';

const toUserSnapshot = (
  data?: ReadUserUiPreferencesQuery,
): TPreferencesSnapshot<TUserUiPreferences> => ({
  blob: (data?.readUserUiPreferences?.uiPreferences ??
    {}) as TUserUiPreferences,
  version: data?.readUserUiPreferences?.objectVersion ?? null,
});

// The single owner of uiPreferences writes: the backend replaces the blob
// wholesale, so one read-merge-submit path stops one change wiping another.
export const useUiPreferencesWriter = (): TUiPreferencesWriter => {
  const { t } = useTranslation();
  const { run } = useSaveToast();

  const user = useAppSelector(selectUser);
  const isAuthenticated = !!user;
  // User filter defaults are keyed by the current tenant (see TUserFilterDefaults).
  const tenantId = user?.attributes?.['custom:tenantId'] ?? '';
  const { refetch: refetchUser } = useReadUserUiPreferencesQuery(undefined, {
    skip: !isAuthenticated,
  });
  const [updateUserUiPreferences] = useUpdateUserUiPreferencesMutation();

  const companyWriter = useFilterPreferencesConfig().useCompanyWriter();

  const messages = {
    loading: t('common:saving_preferences'),
    success: t('common:preferences_saved'),
    error: t('common:preferences_save_error'),
  };

  const userLayer: TPreferencesLayer<TUserUiPreferences> = {
    readFresh: async () => toUserSnapshot(await refetchUser().unwrap()),
    submit: async ({ blob, version }) => {
      await updateUserUiPreferences({
        uiPreferences: blob,
        objectVersion: (version ?? 0) + 1,
      }).unwrap();
    },
  };

  const mergeUserFilterDefaults =
    (update: (defaults?: TFilterDefaults) => TFilterDefaults) =>
    (blob: TUserUiPreferences): TUserUiPreferences => ({
      ...blob,
      filterDefaults: {
        ...blob.filterDefaults,
        [tenantId]: update(blob.filterDefaults?.[tenantId]),
      },
    });

  const withToast = (save: () => Promise<void>) => run(save, messages);

  const commitCompany = (
    merge: (blob: TTenantUiPreferences) => TTenantUiPreferences,
  ) => {
    if (!companyWriter) {
      return Promise.reject(
        new Error('No company preferences layer for this app'),
      );
    }
    return commitPreferences(companyWriter, merge);
  };

  const patch = (
    partial: Partial<TUserUiPreferences>,
    options?: TPatchOptions,
  ) => {
    const save = () =>
      commitPreferences(userLayer, (blob) => ({ ...blob, ...partial }));
    return options?.silent ? save() : withToast(save);
  };

  const patchFilterDefault = (scopeKey: string, param: string, value: string) =>
    withToast(() =>
      commitPreferences(
        userLayer,
        mergeUserFilterDefaults((defaults) =>
          setFilterDefaultInDefaults(defaults, scopeKey, param, value),
        ),
      ),
    );

  const removeFilterDefault = (scopeKey: string, param: string) =>
    withToast(() =>
      commitPreferences(
        userLayer,
        mergeUserFilterDefaults((defaults) =>
          removeFilterDefaultFromDefaults(defaults, scopeKey, param),
        ),
      ),
    );

  const setCompanyFilterDefault = (
    scopeKey: string,
    param: string,
    value: string,
  ) =>
    withToast(() =>
      commitCompany((blob) => ({
        ...blob,
        filterDefaults: setFilterDefaultInDefaults(
          blob.filterDefaults,
          scopeKey,
          param,
          value,
        ),
      })),
    );

  const removeCompanyFilterDefault = (scopeKey: string, param: string) =>
    withToast(() =>
      commitCompany((blob) => ({
        ...blob,
        filterDefaults: removeFilterDefaultFromDefaults(
          blob.filterDefaults,
          scopeKey,
          param,
        ),
      })),
    );

  return {
    patch,
    patchFilterDefault,
    removeFilterDefault,
    setCompanyFilterDefault,
    removeCompanyFilterDefault,
  };
};
